import { supabase, unwrap, fetchAllRows, deleteRecordingFiles } from '../supabase';
import { chunk } from '../utils';
import { saveIdentity } from './contacts';
import type { Exclusion, ExclusionKind } from '../types';

/**
 * Two kinds of exclusion:
 * - untracked: in `excluded_contacts` — the app never syncs calls with this number.
 * - archived: identity flagged `is_archived` — still synced, hidden from lists.
 */
export async function listExclusions(orgId: string): Promise<Exclusion[]> {
  const [untracked, archived] = await Promise.all([
    supabase
      .from('excluded_contacts')
      .select('id, phone_number, name, created_at')
      .eq('org_id', orgId)
      .order('created_at', { ascending: false }),
    supabase
      .from('identities')
      .select('*')
      .eq('org_id', orgId)
      .eq('is_archived', true)
      .order('updated_at', { ascending: false }),
  ]);
  const untrackedRows = (unwrap(untracked) ?? []) as Record<string, unknown>[];
  const archivedRows = (unwrap(archived) ?? []) as Record<string, unknown>[];
  const untrackedPhones = new Set(untrackedRows.map((r) => String(r.phone_number)));
  const history = new Set<string>();
  // Bounded existence checks avoid downloading a number's entire call history.
  for (const batch of chunk([...untrackedPhones], 8)) {
    await Promise.all(
      batch.map(async (phone) => {
        const calls = unwrap(
          await supabase.from('call_log').select('id').eq('org_id', orgId).eq('caller_phone', phone).limit(1),
        );
        if (calls?.length) {
          history.add(phone);
          return;
        }
        const callers = unwrap(
          await supabase.from('callers').select('id').eq('org_id', orgId).eq('phone', phone).limit(1),
        );
        if (callers?.length) history.add(phone);
      }),
    );
  }

  return [
    ...untrackedRows.map((r) => ({
      id: r.id as number,
      phone: String(r.phone_number),
      name: (r.name as string) || null,
      kind: 'untracked' as const,
      createdAt: (r.created_at as string) || null,
      hasHistory: history.has(String(r.phone_number)),
    })),
    ...archivedRows
      .filter((r) => !untrackedPhones.has(String(r.phone)))
      .map((r) => ({
        id: r.id as number,
        phone: String(r.phone),
        name: (r.name as string) || null,
        kind: 'archived' as const,
        createdAt: ((r.updated_at ?? r.created_at) as string) || null,
      })),
  ];
}

export async function addExclusion(
  orgId: string,
  input: { phone: string; name?: string; kind: ExclusionKind },
): Promise<void> {
  const phone = input.phone.trim();
  if (input.kind === 'untracked') {
    unwrap(
      await supabase.from('excluded_contacts').upsert(
        {
          org_id: orgId,
          phone_number: phone,
          name: input.name?.trim() || null,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'org_id,phone_number' },
      ),
    );
  } else {
    await saveIdentity(orgId, phone, {
      isArchived: true,
      ...(input.name?.trim() ? { name: input.name } : {}),
    });
  }
}

export async function removeExclusion(orgId: string, exclusion: Exclusion): Promise<void> {
  if (exclusion.kind === 'untracked') {
    unwrap(await supabase.from('excluded_contacts').delete().eq('org_id', orgId).eq('id', exclusion.id));
  } else {
    unwrap(
      await supabase
        .from('identities')
        .update({ is_archived: false, updated_at: new Date().toISOString() })
        .eq('org_id', orgId)
        .eq('phone', exclusion.phone),
    );
  }
}

export async function getExclusionKind(orgId: string, phone: string): Promise<ExclusionKind | null> {
  const res = await supabase
    .from('excluded_contacts')
    .select('id')
    .eq('org_id', orgId)
    .eq('phone_number', phone)
    .limit(1);
  if (!res.error && res.data?.length) return 'untracked';
  return null;
}

/** Erase stored history for one excluded number, or every excluded number when omitted. */
export async function eraseUntrackedHistory(orgId: string, phone?: string): Promise<number> {
  let excluded = supabase.from('excluded_contacts').select('phone_number').eq('org_id', orgId);
  if (phone !== undefined) excluded = excluded.eq('phone_number', phone);
  const phones = (
    (unwrap(await excluded) ?? []) as {
      phone_number: string;
    }[]
  )
    .map((r) => String(r.phone_number))
    .filter(Boolean);

  for (const batch of chunk(phones, 100)) {
    const recordings = await fetchAllRows<{ recording_url: string | null }>((from, to) =>
      supabase
        .from('call_log')
        .select('recording_url')
        .eq('org_id', orgId)
        .in('caller_phone', batch)
        .not('recording_url', 'is', null)
        .range(from, to),
    );
    await deleteRecordingFiles(recordings.map((r) => r.recording_url));
    unwrap(await supabase.from('call_log').delete().eq('org_id', orgId).in('caller_phone', batch));
    unwrap(await supabase.from('callers').delete().eq('org_id', orgId).in('phone', batch));
  }
  return phones.length;
}
